import React, { useRef, useState } from 'react';

import {
  alpha,
  Badge,
  Button,
  ClickAwayListener,
  Divider,
  Grow,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from '@mui/material';
import { EllipsisVertical } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import { AlineTooltip } from './AlineTooltip';

/** Short enough not to delay a click, long enough to read as the popper growing out of its button. */
const POPPER_TRANSITION_MS = 160;

// Hairline between the sections of a popover, e.g. under the notifications header.
export const POPPER_RULE_COLOR = '#E5E5E8';
// Lighter than `secondaryType`, for metadata that should sit behind the text it annotates.
const MUTED_TYPE_COLOR = '#A1A1AB';
// Softer and wider than `popper`, for a small menu that floats close to what opened it.
const MENU_SHADOW = '0 8px 16px 0 rgba(0, 0, 0, 0.08)';

const useStyles = makeStyles<void, 'rowMenuButton'>()((theme, _params, classes) => ({
  actionButton: {
    borderRadius: 999,
    flexShrink: 0,
    textTransform: 'none',
  },
  /** Compact, and pulled in over the button so it clears the edge of a 44px header. */
  badge: {
    '& .MuiBadge-badge': {
      fontSize: 10,
      height: 16,
      minWidth: 16,
      padding: theme.spacing(0, 0.5),
      transform: 'scale(1) translate(25%, -25%)',
    },
  },
  button: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.rule}`,
    color: theme.appColors.iconsType,
    height: 32,
    width: 32,
  },
  buttonActive: {
    borderColor: theme.appColors.primary,
    color: theme.appColors.primary,
  },
  // Bled out over the paper's own inset, so its 16px inset is measured from the paper's edge.
  header: {
    borderBottom: `1px solid ${POPPER_RULE_COLOR}`,
    margin: theme.spacing(0, -1),
    padding: theme.spacing(2),
  },
  // 18px tall, the title's line, so the header action never makes it taller.
  headerRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    height: 18,
    justifyContent: 'space-between',
  },
  headerTitle: {
    color: theme.appColors.strongType,
    fontSize: 15,
    fontWeight: 700,
    lineHeight: '18px',
    whiteSpace: 'nowrap',
  },
  // Its own section below the header, with the same hairline under it.
  headerToolbar: {
    borderBottom: `1px solid ${POPPER_RULE_COLOR}`,
    margin: theme.spacing(0, -1),
    padding: theme.spacing(1.5),
  },
  paper: {
    borderRadius: 8,
    boxShadow: theme.appShadows.popper,
    maxWidth: 'calc(100vw - 32px)',
    padding: theme.spacing(0, 1, 1),
    width: 400,
  },
  popper: {
    zIndex: theme.zIndex.tooltip,
  },
  row: {
    [`&:hover .${classes.rowMenuButton}, &:focus-within .${classes.rowMenuButton}`]: {
      opacity: 1,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    alignItems: 'center',
    borderRadius: 6,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1),
    transition: theme.transitions.create('background-color', {
      duration: theme.transitions.duration.shortest,
    }),
  },
  /**
   * A row with a read state is a card of its own, so unread and read rows read apart. Tighter on
   * the right, where the unread dot sits in the corner.
   */
  rowCard: {
    alignItems: 'flex-start',
    borderRadius: 8,
    gap: theme.spacing(1.5),
    margin: theme.spacing(0.25, 0),
    padding: theme.spacing(1.5, 1, 1.5, 1.5),
  },
  rowCardSubtitle: {
    color: '#737380',
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.4,
  },
  rowCardTime: {
    fontSize: 11,
    lineHeight: 'normal',
  },
  rowCardTitle: {
    color: theme.appColors.iconsType,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 'normal',
  },
  // A soft wash of the brand color: unread shows without competing with the severity icon.
  rowCardUnread: {
    '&:hover': {
      backgroundColor: alpha(theme.appColors.primary, 0.08),
    },
    backgroundColor: alpha(theme.appColors.primary, 0.04),
  },
  rowClickable: {
    '&:focus-visible': {
      outline: `2px solid ${theme.appColors.primary}`,
      outlineOffset: -2,
    },
    cursor: 'pointer',
  },
  // Controls inside a clickable row keep their own click from reaching the row.
  rowControls: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  rowDot: {
    backgroundColor: theme.appColors.primary,
    borderRadius: '50%',
    height: 6,
    width: 6,
  },
  // A neutral disc keeps icons of different weights and colors reading as one set.
  rowIcon: {
    '& svg': { height: 14, width: 14 },
    alignItems: 'center',
    alignSelf: 'flex-start',
    backgroundColor: theme.appColors.background,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: '50%',
    boxSizing: 'border-box',
    color: theme.appColors.primary,
    display: 'flex',
    flexShrink: 0,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  // A soft wash of the icon's own color, so severity reads at a glance without a heavy outline.
  rowIconTinted: {
    '& svg': { height: 16, width: 16 },
    border: 'none',
    height: 28,
    width: 28,
  },
  rowInner: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  // Above the popper the row lives in, which sits on the tooltip layer.
  rowMenu: {
    zIndex: theme.zIndex.tooltip + 1,
  },
  // Always takes its space, so showing it on hover moves nothing; only its opacity changes.
  rowMenuButton: {
    /**
     * An opaque hover, not the default translucent one: the button covers the unread dot, so a
     * see-through background would show the dot again while the pointer is on the button.
     */
    '&:hover': {
      backgroundColor: theme.appColors.background,
    },
    // A touch screen has no hover to reveal it, so it stays visible there.
    '@media (hover: none)': {
      opacity: 1,
    },
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    color: theme.appColors.iconsType,
    height: 24,
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.shortest,
    }),
    width: 24,
  },
  rowMenuButtonOpen: {
    opacity: 1,
  },
  // Opaque, on top and centered on the unread dot, so while it shows it covers the dot.
  rowMenuButtonOverlay: {
    left: '50%',
    position: 'absolute',
    top: 3,
    transform: 'translate(-50%, -50%)',
    zIndex: 1,
  },
  rowMenuDivider: {
    borderColor: POPPER_RULE_COLOR,
    margin: `${theme.spacing(0.25, 0)} !important`,
  },
  rowMenuItem: {
    '& svg': { flexShrink: 0, height: 14, marginTop: 1, width: 14 },
    alignItems: 'flex-start',
    borderRadius: 4,
    color: '#1E293B',
    fontSize: 12,
    fontWeight: 400,
    gap: theme.spacing(1),
    lineHeight: 'normal',
    minHeight: 0,
    padding: theme.spacing(0.75, 1),
  },
  rowMenuItemDanger: {
    color: '#EF4444',
    fontWeight: 500,
  },
  rowMenuItemDescription: {
    color: theme.appColors.secondaryType,
    fontSize: 12,
    lineHeight: '16px',
  },
  rowMenuItemText: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    whiteSpace: 'normal',
  },
  // A fixed width, so a long "cannot remove" reason wraps instead of stretching the menu.
  rowMenuList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.75),
  },
  rowMenuPaper: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 8,
    boxShadow: MENU_SHADOW,
    width: 180,
  },
  /**
   * Holds the unread dot at the card's top corner, with the "..." button laid over it, at a fixed
   * size so nothing shifts. The button is wider than the slot and spills into the card's padding.
   */
  rowSlot: {
    alignItems: 'flex-start',
    display: 'flex',
    flexShrink: 0,
    height: 20,
    justifyContent: 'center',
    position: 'relative',
    width: 12,
  },
  // Up to two lines, enough for what happened and the fix; the tooltip has the rest.
  rowSubtitle: {
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    color: theme.appColors.secondaryType,
    display: '-webkit-box',
    fontSize: 12,
    lineHeight: '16px',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  // Muted behind the subtitle it sits beside; 18px keeps it on the title's baseline.
  rowTime: {
    color: MUTED_TYPE_COLOR,
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '18px',
  },
  // One line, so a long title cannot make its row taller than the rest.
  rowTitle: {
    color: theme.appColors.iconsType,
    flexGrow: 1,
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '18px',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowTitleLine: {
    alignItems: 'baseline',
    display: 'flex',
    gap: theme.spacing(1),
  },
  rowTitleRead: {
    fontWeight: 500,
  },
  rowTooltip: {
    fontSize: 12,
    maxWidth: 320,
    textAlign: 'left',
  },
  rowTooltipPopper: {
    zIndex: theme.zIndex.tooltip + 1,
  },
  rowTooltipText: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
}));

export interface AlertsPopperButtonProps {
  readonly children: React.ReactNode;
  /** Shown on the badge; the badge hides at 0. */
  readonly count: number;
  readonly icon: React.ReactNode;
  /** The tooltip, the button's accessible name, and the popper's header. */
  readonly label: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly open: boolean;
  readonly buttonDataCy?: string;
  /** Drawn at the right end of the popper's header, e.g. a "Mark all as read" button. */
  readonly headerAction?: React.ReactNode;
  /** Drawn below the header, above the rows, e.g. filter chips. */
  readonly headerToolbar?: React.ReactNode;
  /**
   * Keeps the rows mounted while closed, so rows that animate in do not replay it on every open,
   * and anything a row owns (such as a dialog it opened) outlives the popper.
   */
  readonly keepMounted?: boolean;
  readonly popperDataCy?: string;
}

/** An outlined icon button with a count badge that opens a list of alerts below it. */
export function AlertsPopperButton({
  buttonDataCy,
  children,
  count,
  headerAction,
  headerToolbar,
  icon,
  keepMounted = false,
  label,
  onOpenChange,
  open,
  popperDataCy,
}: AlertsPopperButtonProps): JSX.Element {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  /**
   * React-tree aware, so a click inside a row's menu (portaled to the body) still counts as inside.
   * The bell button toggles on its own; closing here too would reopen it on the same click.
   */
  const onClickAway = (event: MouseEvent | TouchEvent) => {
    if (!open) return;
    if (event.target instanceof Node && anchorEl?.contains(event.target)) return;
    onOpenChange(false);
  };

  return (
    <>
      <Badge badgeContent={count} className={classes.badge} color="primary">
        {/* No tooltip while open: it would sit on top of the list it names. */}
        <AlineTooltip arrow placement="top" title={open ? '' : label}>
          <IconButton
            ref={setAnchorEl}
            aria-label={label}
            className={cx(classes.button, open && classes.buttonActive)}
            data-cy={buttonDataCy}
            size="small"
            onClick={() => onOpenChange(!open)}
          >
            {icon}
          </IconButton>
        </AlineTooltip>
      </Badge>
      <Popper
        transition
        anchorEl={anchorEl}
        className={classes.popper}
        keepMounted={keepMounted}
        modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        open={open}
        placement="bottom-end"
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={onClickAway}>
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: 'top right' }}
              timeout={POPPER_TRANSITION_MS}
            >
              <Paper className={classes.paper} data-cy={popperDataCy}>
                <div className={classes.header}>
                  <div className={classes.headerRow}>
                    <Typography className={classes.headerTitle} variant="subtitle1">
                      {label}
                    </Typography>
                    {headerAction}
                  </div>
                </div>
                {Boolean(headerToolbar) && (
                  <div className={classes.headerToolbar}>{headerToolbar}</div>
                )}
                {children}
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}

export interface AlertsPopperRowMenuItem {
  readonly icon: React.ReactNode;
  readonly label: string;
  /** A smaller second line under the label, e.g. why the action is unavailable. */
  readonly description?: string;
  /** Omitted for an item that only explains something; it renders disabled. */
  readonly onClick?: () => void;
  /** `danger` for an action that takes something away, like removing the notification. */
  readonly tone?: 'danger';
}

export interface AlertsPopperRowProps {
  readonly icon: React.ReactNode;
  readonly title: React.ReactNode;
  /** The one thing to do about it, typically an `AlertsPopperAction` pill. */
  readonly action?: React.ReactNode;
  readonly dataCy?: string;
  /** The disc behind a colored icon; defaults to a wash of `iconColor`. */
  readonly iconBackground?: string;
  /** Colors the icon and washes its disc in the same color; omit for the neutral disc. */
  readonly iconColor?: string;
  /**
   * Adds a "..." button that shows on hover and opens these actions. Passing an empty list keeps
   * the button's space, so rows with and without actions line up.
   */
  readonly menuItems?: readonly AlertsPopperRowMenuItem[];
  /** Makes the whole row a link to where the alert is resolved. */
  readonly onClick?: () => void;
  readonly subtitle?: React.ReactNode;
  /** How long ago it happened, shown small at the right end of the title line, e.g. "5m". */
  readonly time?: string;
  /**
   * The viewer's read state. Unread rows get a tinted card, a bolder title and a dot; read rows a
   * plain card. Omit for alerts that have no read state.
   */
  readonly unread?: boolean;
}

/** One alert in an `AlertsPopperButton`: icon, title over subtitle, and an optional action. */
export function AlertsPopperRow({
  action,
  dataCy,
  icon,
  iconBackground,
  iconColor,
  menuItems,
  onClick,
  subtitle,
  time,
  title,
  unread,
}: AlertsPopperRowProps): JSX.Element {
  const { classes, cx } = useStyles();
  const hasReadState = unread !== undefined;
  const titleRef = useRef<HTMLParagraphElement>(null);
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const measureTruncation = () =>
    setIsTruncated(
      [titleRef.current, subtitleRef.current].some(
        element =>
          element !== null &&
          (element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight)
      )
    );
  const fullText = (
    <span className={classes.rowTooltipText}>
      <strong>{title}</strong>
      {Boolean(subtitle) && <span>{subtitle}</span>}
    </span>
  );

  return (
    <div
      className={cx(
        classes.row,
        hasReadState && classes.rowCard,
        unread && classes.rowCardUnread,
        onClick && classes.rowClickable
      )}
      data-cy={dataCy}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={event => {
        if (onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <span
        className={cx(classes.rowIcon, Boolean(iconColor) && classes.rowIconTinted)}
        style={
          iconColor
            ? { backgroundColor: iconBackground ?? alpha(iconColor, 0.12), color: iconColor }
            : undefined
        }
      >
        {icon}
      </span>
      <AlineTooltip
        placement="bottom-start"
        slotProps={{
          popper: { className: classes.rowTooltipPopper },
          tooltip: { className: classes.rowTooltip },
        }}
        title={isTruncated ? fullText : ''}
      >
        <div className={classes.rowInner} onMouseEnter={measureTruncation}>
          <div className={classes.rowTitleLine}>
            <Typography
              ref={titleRef}
              className={cx(
                classes.rowTitle,
                hasReadState && classes.rowCardTitle,
                unread === false && classes.rowTitleRead
              )}
            >
              {title}
            </Typography>
            {Boolean(time) && (
              <Typography className={cx(classes.rowTime, hasReadState && classes.rowCardTime)}>
                {time}
              </Typography>
            )}
          </div>
          {Boolean(subtitle) && (
            <Typography
              ref={subtitleRef}
              className={cx(classes.rowSubtitle, hasReadState && classes.rowCardSubtitle)}
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </AlineTooltip>
      <RowControls action={action} menuItems={menuItems} unread={unread} />
    </div>
  );
}

/** The small outlined pill an `AlertsPopperRow` uses for its action. */
export function AlertsPopperAction({
  children,
  dataCy,
  disabled = false,
  onClick,
}: {
  readonly children: React.ReactNode;
  readonly onClick: () => void;
  readonly dataCy?: string;
  readonly disabled?: boolean;
}): JSX.Element {
  const { classes } = useStyles();
  return (
    <Button
      className={classes.actionButton}
      color="primary"
      data-cy={dataCy}
      disabled={disabled}
      size="small"
      variant="outlined"
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/**
 * A row with a read state ends in one fixed slot: the unread dot, with the "..." button over it.
 * Any other row ends in its action and, when it has one, the "..." button.
 */
function RowControls({
  action,
  menuItems,
  unread,
}: Pick<AlertsPopperRowProps, 'action' | 'menuItems' | 'unread'>): JSX.Element | null {
  const { classes } = useStyles();
  const stopClick = (event: React.SyntheticEvent) => event.stopPropagation();

  if (unread !== undefined)
    return (
      <span className={classes.rowSlot} onClick={stopClick} onKeyDown={stopClick}>
        {unread && <span aria-label="Unread" className={classes.rowDot} />}
        {menuItems && <RowMenu overlay items={menuItems} />}
      </span>
    );

  if (!action && !menuItems) return null;
  return (
    <span className={classes.rowControls} onClick={stopClick} onKeyDown={stopClick}>
      {action}
      {menuItems && <RowMenu items={menuItems} />}
    </span>
  );
}

function RowMenu({
  items,
  overlay = false,
}: {
  readonly items: readonly AlertsPopperRowMenuItem[];
  readonly overlay?: boolean;
}) {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="More actions"
        className={cx(
          classes.rowMenuButton,
          overlay && classes.rowMenuButtonOverlay,
          open && classes.rowMenuButtonOpen
        )}
        disabled={items.length === 0}
        size="small"
        style={items.length === 0 ? { visibility: 'hidden' } : undefined}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        <EllipsisVertical size={14} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        className={classes.rowMenu}
        classes={{ list: classes.rowMenuList, paper: classes.rowMenuPaper }}
        open={open}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setAnchorEl(null)}
      >
        {/* Flat, not in fragments: MUI's Menu does not accept a Fragment as a child. */}
        {items.flatMap((item, index) => [
          // Sets an item that only explains something apart from the actions above it.
          !item.onClick && index > 0 && (
            <Divider key={`${item.label}-divider`} className={classes.rowMenuDivider} />
          ),
          <MenuItem
            key={item.label}
            className={cx(classes.rowMenuItem, item.tone === 'danger' && classes.rowMenuItemDanger)}
            disabled={!item.onClick}
            onClick={() => {
              setAnchorEl(null);
              item.onClick?.();
            }}
          >
            {item.icon}
            <span className={classes.rowMenuItemText}>
              {item.label}
              {Boolean(item.description) && (
                <span className={classes.rowMenuItemDescription}>{item.description}</span>
              )}
            </span>
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}
